// MonitoringSystem.jsx - Version complète corrigée
import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { APP_CONFIG } from '../../config/appConfig';

// ✅ Définition du composant MetricCard (NE PAS METTRE DE COMMENTAIRE ICI)
const MetricCard = ({ label, value, status, icon }) => (
  <div className="bg-white p-5 rounded-2xl border border-[#e9ecef] shadow-sm hover:shadow-md transition-all duration-200 relative">
    <div className="flex justify-between items-center mb-3">
      <span className="text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider">
        {label}
      </span>
      <span className={`h-2.5 w-2.5 rounded-full ${status === 'ok' ? 'bg-[#009640]' : 'bg-[#ef7c00]'} animate-pulse`}></span>
    </div>
    <div className="text-2xl font-serif font-bold text-[#212529]">
      {value || '0'}
    </div>
    {icon && (
      <div className="text-[#009640]/10 text-3xl absolute bottom-2 right-3">
        {icon}
      </div>
    )}
  </div>
);

export default function MonitoringSystem() {
  const { token } = useAuth(); 
  
  const [metrics, setMetrics] = useState({ 
    cpuLoad: '0%', 
    ramUsage: '0MB', 
    uptime: '...',
    activeSessions: '0' 
  });

  const [history, setHistory] = useState([]);
  const [logs, setLogs] = useState([
    { id: 1, time: new Date().toLocaleTimeString(), msg: "Initialisation du moniteur...", type: "info" }
  ]);

  useEffect(() => {
    const fetchData = () => {
      if (!token) return;

      // ✅ Utiliser le bon endpoint /api/v1/config/monitor/stats
      fetch(`${APP_CONFIG.BASE_URL}/api/v1/config/monitor/stats`, {
        headers: { "Authorization": `Bearer ${token}` }
      })
      .then(res => {
        if (!res.ok) throw new Error(`Erreur HTTP: ${res.status}`);
        return res.json();
      })
      .then(data => {
        // ✅ Adapter la récupération des données selon la structure retournée
        const cpuValue = parseFloat(data.cpuLoad?.replace('%', '')) || 0;
        const ramValue = parseInt(data.ramUsage?.split('MB')[0]) || 0;
        const uptime = data.uptime || '...';
        const activeSessions = data.activeSessions || '0';

        setMetrics({
          cpuLoad: data.cpuLoad || '0%',
          ramUsage: data.ramUsage || '0MB',
          uptime: uptime,
          activeSessions: activeSessions
        });

        const newDataPoint = {
          time: new Date().toLocaleTimeString().split(' ')[0],
          cpu: cpuValue,
          ram: ramValue
        };

        setHistory(prev => [...prev, newDataPoint].slice(-20));
        
        setLogs(prev => [{
          id: Date.now(),
          time: new Date().toLocaleTimeString(),
          msg: `Sync : CPU ${cpuValue}% | RAM ${ramValue}MB`,
          type: "system"
        }, ...prev].slice(0, 10));
      })
      .catch(err => {
        const errorMessage = err instanceof Error ? err.message : "Erreur inconnue";
        
        setLogs(prev => [{
          id: Date.now(),
          time: new Date().toLocaleTimeString(),
          msg: `Échec API: ${errorMessage}`,
          type: "error"
        }, ...prev].slice(0, 10));
      });
    };

    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [token]);

  return (
    <div className="space-y-6 text-left font-serif">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-[#e9ecef] pb-4">
        <div>
          <h2 className="text-3xl font-serif font-bold text-[#212529]">
            Monitoring Système
          </h2>
          <p className="text-sm text-[#6c757d] font-serif">
            Analyse comparative des ressources en temps réel
          </p>
        </div>
        <div className="flex items-center gap-3 bg-[#f8f9fa] px-4 py-2 rounded-xl text-[10px] font-serif font-bold text-[#495057]">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#009640' }}></div>
          CPU
          <div className="w-2.5 h-2.5 rounded-full ml-2" style={{ backgroundColor: '#ef7c00' }}></div>
          RAM
        </div>
      </div>

      {/* Métriques - ✅ Utilisation de MetricCard */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard label="Charge CPU" value={metrics.cpuLoad} status="ok" icon="⚡" />
        <MetricCard label="Mémoire RAM" value={metrics.ramUsage} status="ok" icon="💾" />
        <MetricCard label="Sessions actives" value={metrics.activeSessions || '0'} status="ok" icon="👥" />
        <MetricCard label="Uptime" value={metrics.uptime} status="ok" icon="⏱️" />
      </div>

      {/* Graphique */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#e9ecef]">
        <h3 className="text-sm font-serif font-bold text-[#343a40] mb-6 uppercase tracking-wider">
          Performance en temps réel
        </h3>
        <div className="h-72 w-full" style={{ position: 'relative', minHeight: '288px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={history}>
              <defs>
                <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#009640" stopOpacity={0.25}/>
                  <stop offset="95%" stopColor="#009640" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorRam" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef7c00" stopOpacity={0.25}/>
                  <stop offset="95%" stopColor="#ef7c00" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e9ecef" />
              <XAxis dataKey="time" fontSize={10} tickMargin={10} stroke="#adb5bd" fontFamily="Georgia, serif" />
              <YAxis yAxisId="left" fontSize={10} stroke="#009640" fontFamily="Georgia, serif" />
              <YAxis yAxisId="right" orientation="right" fontSize={10} stroke="#ef7c00" fontFamily="Georgia, serif" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#212529', 
                  border: 'none', 
                  borderRadius: '12px', 
                  color: '#fff', 
                  fontSize: '11px',
                  fontFamily: 'Georgia, serif'
                }}
              />
              <Legend verticalAlign="top" height={36} iconType="circle" />
              <Area 
                yAxisId="left"
                type="monotone" 
                dataKey="cpu" 
                stroke="#009640" 
                strokeWidth={3}
                fill="url(#colorCpu)" 
                name="Charge CPU (%)"
              />
              <Area 
                yAxisId="right"
                type="monotone" 
                dataKey="ram" 
                stroke="#ef7c00" 
                strokeWidth={3}
                fill="url(#colorRam)" 
                name="Usage RAM (MB)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

	  
      {/* Logs système */}
      <div className="bg-[#212529] rounded-2xl border border-[#343a40] overflow-hidden shadow-xl">
        <div className="bg-[#343a40]/50 px-4 py-3 border-b border-[#343a40]">
          <span className="text-[#adb5bd] text-[10px] font-serif font-black uppercase tracking-wider">
            Flux d'événements système
          </span>
        </div>
        <div className="p-5 font-serif text-xs h-48 overflow-y-auto space-y-2">
          {logs.map(log => (
            <div key={log.id} className="flex gap-4 border-l border-[#343a40] pl-4">
              <span className="text-[#6c757d] shrink-0 font-serif">{log.time}</span>
              <span className={`${log.type === 'error' ? 'text-[#ef7c00]' : 'text-[#009640]'} font-bold font-serif`}>
                [{log.type.toUpperCase()}]
              </span>
              <span className="text-[#ced4da] italic font-serif">{log.msg}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}